import React, { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useResetScrollOnStepChange } from '@/hooks/useResetScrollOnStepChange';
import { Button } from '@/components/ui/Button';
import { ProgressIndicator } from '../CampaignCreation/ProgressIndicator';
import { DisplayAdCreationStep, type DisplayAdFormData } from './steps/DisplayAdCreationStep';
import { debug } from '@/utils/debug';
import { isUrlGloballyAuthorized } from '@/utils/urlUtils';
import { useAuthStore } from '@/store/authStore';
import type { Campaign } from '@/types/campaign';
import type { AppConfiguration } from '@/types/api';
import apiClient from '@/services/api/client';
import { useMaterializePendingTargeted } from '@/hooks/useMaterializePendingTargeted';
import { batchDeleteImages } from '@/services/api/media';

interface AdGroup {
  ad_group_id?: string;
  ads_group_id?: string;
  ad_group_name?: string;
  ads_group_name?: string;
}

interface CreateRDAWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  campaignId: string;
  campaignName: string;
  adGroups: AdGroup[];
}

interface FormErrors {
  adGroup?: string;
  landscapeImages?: string;
  squareImages?: string;
  shortHeadlines?: string;
  longHeadline?: string;
  descriptions?: string;
  businessName?: string;
  finalUrl?: string;
}

// API types for RDA creation
interface CreateRDARequest {
  ad_group_id: string;
  headlines: string[];
  long_headline: string;
  descriptions: string[];
  business_name: string;
  final_url: string;
  landscape_image_ids: string[];
  square_image_ids: string[];
  logo_square_ids?: string[];
  logo_landscape_ids?: string[];
}

interface CreateRDAResponse {
  success: boolean;
  ad_id: string;
  resource_name: string;
  message: string;
}

const wizardSteps = [
  { label: 'Ad Group', description: '' },
  { label: 'Display Ad', description: '' },
  { label: 'Review', description: '' },
];

export const CreateRDAWizard: React.FC<CreateRDAWizardProps> = ({
  isOpen,
  onClose,
  onSuccess,
  campaignId,
  campaignName,
  adGroups,
}) => {
  const queryClient = useQueryClient();
  const { memberUuid } = useAuthStore();
  const [currentStep, setCurrentStep] = useState(1);
  const scrollBodyRef = useRef<HTMLDivElement>(null);
  useResetScrollOnStepChange(currentStep, scrollBodyRef);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const materializePending = useMaterializePendingTargeted();
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Selected ad group
  const [selectedAdGroupId, setSelectedAdGroupId] = useState<string>('');

  // RDA form data
  const [formData, setFormData] = useState<DisplayAdFormData>({
    landscapePending: [],
    squarePending: [],
    logoSquarePending: [],
    logoLandscapePending: [],
    shortHeadlines: [''],
    longHeadline: '',
    descriptions: [''],
    businessName: '',
    finalUrl: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});

  // Get selected ad group details
  const selectedAdGroup = adGroups.find(
    (ag) => (ag.ad_group_id || ag.ads_group_id) === selectedAdGroupId
  );
  const selectedAdGroupName = selectedAdGroup?.ad_group_name || selectedAdGroup?.ads_group_name || '';

  // Reset form when wizard opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setErrors({});
      setSubmitError(null);
      setSelectedAdGroupId('');
      setFormData({
        landscapePending: [],
        squarePending: [],
        logoSquarePending: [],
        logoLandscapePending: [],
        shortHeadlines: [''],
        longHeadline: '',
        descriptions: [''],
        businessName: '',
        finalUrl: '',
      });
    }
  }, [isOpen]);

  // Validation function for each step
  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    if (step === 1) {
      // Validate ad group selection
      if (!selectedAdGroupId) {
        newErrors.adGroup = 'Please select an ad group';
      }
    }

    if (step === 2) {
      // Validate RDA fields
      if (formData.landscapePending.length === 0) {
        newErrors.landscapeImages = 'At least 1 landscape image is required';
      }
      if (formData.squarePending.length === 0) {
        newErrors.squareImages = 'At least 1 square image is required';
      }

      const nonEmptyShortHeadlines = formData.shortHeadlines.filter((h) => h.trim().length > 0);
      if (nonEmptyShortHeadlines.length < 1) {
        newErrors.shortHeadlines = 'At least 1 short headline is required';
      } else if (formData.shortHeadlines.some((h) => h.trim().length > 0 && h.trim().length > 30)) {
        newErrors.shortHeadlines = 'Short headlines must be 30 characters or less';
      }

      if (!formData.longHeadline.trim()) {
        newErrors.longHeadline = 'Long headline is required';
      } else if (formData.longHeadline.length > 90) {
        newErrors.longHeadline = 'Long headline must be 90 characters or less';
      }

      const nonEmptyDescriptions = formData.descriptions.filter((d) => d.trim().length > 0);
      if (nonEmptyDescriptions.length < 1) {
        newErrors.descriptions = 'At least 1 description is required';
      } else if (formData.descriptions.some((d) => d.trim().length > 0 && d.trim().length > 90)) {
        newErrors.descriptions = 'Descriptions must be 90 characters or less';
      }

      if (!formData.businessName.trim()) {
        newErrors.businessName = 'Business name is required';
      } else if (formData.businessName.length > 25) {
        newErrors.businessName = 'Business name must be 25 characters or less';
      }

      if (!formData.finalUrl.trim()) {
        newErrors.finalUrl = 'Final URL is required';
      } else if (!formData.finalUrl.startsWith('http://') && !formData.finalUrl.startsWith('https://')) {
        newErrors.finalUrl = 'Final URL must start with http:// or https://';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, wizardSteps.length));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleStepClick = (step: number) => {
    if (step <= currentStep) {
      setCurrentStep(step);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Materialize pending crops just before submit (no Targeted rows
      // exist until this point — abandoned wizard runs leave no orphans).
      // `materializePending` cleans up partial failures within a bucket;
      // the inner try/catch below cleans up across all 4 buckets if the
      // RDA create call fails.
      const [landscapeM, squareM, logoSquareM, logoLandscapeM] = await Promise.all([
        materializePending(formData.landscapePending),
        materializePending(formData.squarePending),
        materializePending(formData.logoSquarePending),
        materializePending(formData.logoLandscapePending),
      ]);
      const allMaterializedIds = [
        ...landscapeM, ...squareM, ...logoSquareM, ...logoLandscapeM,
      ].map((t) => t.id);
      const cleanupMaterialized = async () => {
        if (allMaterializedIds.length === 0 || !memberUuid) return;
        try {
          await batchDeleteImages({
            member_uuid: memberUuid,
            image_ids: allMaterializedIds,
          });
        } catch (cleanupErr) {
          debug.error('CreateRDAWizard: cleanup of materialized Targeted ids failed', cleanupErr);
        }
      };

      try {
      // Build API request
      const request: CreateRDARequest = {
        ad_group_id: selectedAdGroupId,
        headlines: formData.shortHeadlines.filter((h) => h.trim().length > 0),
        long_headline: formData.longHeadline.trim(),
        descriptions: formData.descriptions.filter((d) => d.trim().length > 0),
        business_name: formData.businessName.trim(),
        final_url: formData.finalUrl.trim(),
        landscape_image_ids: landscapeM.map((t) => t.id),
        square_image_ids: squareM.map((t) => t.id),
        logo_square_ids: logoSquareM.length > 0 ? logoSquareM.map((t) => t.id) : undefined,
        logo_landscape_ids: logoLandscapeM.length > 0 ? logoLandscapeM.map((t) => t.id) : undefined,
      };

      debug.log('CreateRDAWizard: Submitting RDA creation request', { request, campaignId, selectedAdGroupId });

      // Call the API to create the RDA
      const response = await apiClient.post<CreateRDAResponse>(
        `/campaigns/${campaignId}/ad-groups/${selectedAdGroupId}/ads/rda`,
        request,
        { timeout: 180000 } // 3 minutes for image uploads
      );

      debug.log('CreateRDAWizard: RDA created successfully', response.data);

      // Check if URL is globally authorized in existing config
      const existingConfig = memberUuid ? queryClient.getQueryData<AppConfiguration>(['app-config', memberUuid]) : null;
      const urlIsAuthorized = isUrlGloballyAuthorized(formData.finalUrl, existingConfig);
      debug.log('CreateRDAWizard: URL authorization check', { url: formData.finalUrl, urlIsAuthorized });

      // Build optimistic ad data (matching the structure used by ad group wizard)
      const optimisticAd = {
        ad_id: response.data.ad_id || `optimistic_${Date.now()}`,
        ad_type: 'RESPONSIVE_DISPLAY_AD',
        ad_name: formData.longHeadline || formData.shortHeadlines[0] || 'New Display Ad',
        status: 'ENABLED' as const,
        headlines: formData.shortHeadlines.filter((h) => h.trim()),
        descriptions: formData.descriptions.filter((d) => d.trim()),
        final_urls: [{ url: formData.finalUrl, flouri_scraping_enabled: urlIsAuthorized }],
        rda_headlines: formData.shortHeadlines.filter((h) => h.trim()),
        rda_descriptions: formData.descriptions.filter((d) => d.trim()),
        rda_long_headline: formData.longHeadline,
        rda_business_name: formData.businessName,
        rda_marketing_images: landscapeM.map((t) => t.id),
        rda_square_marketing_images: squareM.map((t) => t.id),
        rda_logo_images: logoSquareM.map((t) => t.id),
      };

      // Optimistically update the campaign-from-cache query
      queryClient.setQueryData<Campaign>(['campaign-from-cache', campaignId], (oldData) => {
        if (!oldData) {
          debug.log('CreateRDAWizard: No oldData for campaign-from-cache');
          return oldData;
        }
        debug.log('CreateRDAWizard: Updating campaign-from-cache', {
          campaignId,
          selectedAdGroupId,
          adGroupsCount: oldData.ads_group_list?.length,
          adGroupIds: oldData.ads_group_list?.map(ag => ag.ad_group_id || ag.ads_group_id),
        });
        const updated = {
          ...oldData,
          ads_group_list: oldData.ads_group_list?.map((ag) => {
            if (ag.ad_group_id === selectedAdGroupId || ag.ads_group_id === selectedAdGroupId) {
              debug.log('CreateRDAWizard: Found matching ad group, adding ad', {
                adGroupId: ag.ad_group_id || ag.ads_group_id,
                existingAdsCount: (ag.ads || []).length,
              });
              return {
                ...ag,
                ads: [...(ag.ads || []), optimisticAd],
                ads_list: [...(ag.ads_list || []), optimisticAd],
              };
            }
            return ag;
          }),
        } as Campaign;
        debug.log('CreateRDAWizard: Updated campaign-from-cache', {
          newAdGroupsCount: updated.ads_group_list?.length,
        });
        return updated;
      });

      // Also update app-config cache (this is the source of truth for useCampaignFromCache)
      debug.log('CreateRDAWizard: Updating app-config cache', { memberUuid, campaignId });
      if (memberUuid) {
        queryClient.setQueryData<AppConfiguration>(['app-config', memberUuid], (oldConfig) => {
          if (!oldConfig?.configuration?.campaign_list) {
            debug.log('CreateRDAWizard: No oldConfig for app-config');
            return oldConfig;
          }
          debug.log('CreateRDAWizard: Found app-config, updating campaign_list');
          return {
            ...oldConfig,
            configuration: {
              ...oldConfig.configuration,
              campaign_list: oldConfig.configuration.campaign_list.map((camp) => {
                if (String(camp.campaign_id) === String(campaignId)) {
                  debug.log('CreateRDAWizard: Found campaign in app-config, updating ads_group_list');
                  return {
                    ...camp,
                    ads_group_list: camp.ads_group_list?.map((ag) => {
                      if (ag.ad_group_id === selectedAdGroupId || ag.ads_group_id === selectedAdGroupId) {
                        debug.log('CreateRDAWizard: Found ad group in app-config, adding ad');
                        return {
                          ...ag,
                          ads: [...(ag.ads || []), optimisticAd],
                          ads_list: [...(ag.ads_list || []), optimisticAd],
                        };
                      }
                      return ag;
                    }),
                  };
                }
                return camp;
              }),
            },
          };
        });
      } else {
        debug.log('CreateRDAWizard: No memberUuid, skipping app-config update');
      }

      debug.log('CreateRDAWizard: Optimistically updated caches');

      // DON'T invalidate app-config - that would trigger a refetch from Google Ads which
      // may not have propagated the new ad yet, overwriting our optimistic update.
      // Instead, just invalidate campaign-from-cache to trigger a re-derive from the
      // already-updated app-config cache.
      queryClient.invalidateQueries({ queryKey: ['campaign-from-cache', campaignId] });

      await onSuccess();
      onClose();
      } catch (submitErr) {
        await cleanupMaterialized();
        throw submitErr;
      }
    } catch (error: unknown) {
      debug.error('CreateRDAWizard: Failed to create RDA', error);

      let errorMessage = 'Failed to create display ad. Please try again.';

      const apiError = error as {
        response?: { data?: { detail?: string; message?: string; error?: string } };
        message?: string;
      };
      if (apiError.response?.data) {
        const data = apiError.response.data;
        errorMessage = data.detail || data.message || data.error || errorMessage;

        if (typeof errorMessage === 'object') {
          errorMessage = JSON.stringify(errorMessage);
        }
      } else if (apiError.message) {
        errorMessage = apiError.message;
      }

      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-8 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          disabled={isSubmitting}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Create New Responsive Display Ad</h2>
          <p className="text-sm text-gray-600 mt-1">
            Add a display ad to <span className="font-medium">{campaignName}</span>
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="px-6 py-2 border-b border-gray-100">
          <ProgressIndicator
            currentStep={currentStep}
            totalSteps={wizardSteps.length}
            steps={wizardSteps}
            onStepClick={handleStepClick}
          />
        </div>

        {/* Step Content */}
        <div
          ref={scrollBodyRef}
          className="px-6 py-6 overflow-y-auto"
          style={{ maxHeight: 'calc(100vh - 400px)', minHeight: '400px' }}
        >
          {/* Step 1: Select Ad Group */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Ad Group</h3>
                <p className="text-sm text-gray-600">
                  Choose which ad group to add the new display ad to.
                </p>
              </div>

              <div className="space-y-3">
                {adGroups.map((ag) => {
                  const agId = ag.ad_group_id || ag.ads_group_id || '';
                  const agName = ag.ad_group_name || ag.ads_group_name || 'Unnamed Ad Group';
                  const isSelected = selectedAdGroupId === agId;

                  return (
                    <label
                      key={agId}
                      className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="adGroup"
                        value={agId}
                        checked={isSelected}
                        onChange={(e) => setSelectedAdGroupId(e.target.value)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="ml-3 text-sm font-medium text-gray-900">{agName}</span>
                    </label>
                  );
                })}
              </div>

              {errors.adGroup && (
                <p className="text-sm text-red-600">{errors.adGroup}</p>
              )}
            </div>
          )}

          {/* Step 2: Display Ad Creation */}
          {currentStep === 2 && (
            <DisplayAdCreationStep
              formData={formData}
              onChangeFormData={(updates) => setFormData((prev) => ({ ...prev, ...updates }))}
              errors={{
                landscapeImages: errors.landscapeImages,
                squareImages: errors.squareImages,
                shortHeadlines: errors.shortHeadlines,
                longHeadline: errors.longHeadline,
                descriptions: errors.descriptions,
                businessName: errors.businessName,
                finalUrl: errors.finalUrl,
              }}
            />
          )}

          {/* Step 3: Review */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Review Your Display Ad</h3>

              {/* Ad Group Info */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Target Ad Group</h4>
                <p className="text-sm text-gray-900">{selectedAdGroupName}</p>
              </div>

              {/* Images Summary */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Images</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Landscape (1.91:1):</span>{' '}
                    <span className="text-gray-900">{formData.landscapePending.length} images</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Square (1:1):</span>{' '}
                    <span className="text-gray-900">{formData.squarePending.length} images</span>
                  </div>
                  {formData.logoSquarePending.length > 0 && (
                    <div>
                      <span className="text-gray-500">Square Logo:</span>{' '}
                      <span className="text-gray-900">{formData.logoSquarePending.length} logos</span>
                    </div>
                  )}
                  {formData.logoLandscapePending.length > 0 && (
                    <div>
                      <span className="text-gray-500">Landscape Logo:</span>{' '}
                      <span className="text-gray-900">{formData.logoLandscapePending.length} logos</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Text Assets Summary */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Text Assets</h4>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-gray-500">Business Name:</span>{' '}
                    <span className="text-gray-900">{formData.businessName}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Final URL:</span>{' '}
                    <span className="text-gray-900 break-all">{formData.finalUrl}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Short Headlines:</span>
                    <ul className="mt-1 list-disc list-inside text-gray-900">
                      {formData.shortHeadlines.filter(h => h.trim()).map((h, i) => (
                        <li key={i}>{h}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <span className="text-gray-500">Long Headline:</span>{' '}
                    <span className="text-gray-900">{formData.longHeadline}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Descriptions:</span>
                    <ul className="mt-1 list-disc list-inside text-gray-900">
                      {formData.descriptions.filter(d => d.trim()).map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          {submitError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{submitError}</p>
            </div>
          )}

          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Step {currentStep} of {wizardSteps.length}
            </div>

            <div className="flex space-x-3">
              {currentStep > 1 && (
                <Button
                  variant="secondary"
                  onClick={handleBack}
                  disabled={isSubmitting}
                >
                  Back
                </Button>
              )}

              {currentStep < wizardSteps.length ? (
                <Button
                  variant="primary"
                  onClick={handleNext}
                  disabled={isSubmitting}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Display Ad'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
